export const admin = {
  // Dashboard
  dashboardTitle: 'Admin Dashboard',
  dashboardSubtitle: 'Entry point for management features. All views here are read-only (edits happen on each dedicated screen).',
  adminBadge: 'Admin',
  loggedInAs: 'Signed in',

  // Entry link cards
  linksTitle: 'Management',
  matchingTitle: 'Matching',
  matchingDesc: 'Pair and confirm lesson requests',
  wordPoolTitle: 'Word Pool Review',
  wordPoolDesc: 'Review submitted words, approve organization applications',
  levelCheckTitle: 'Level Check Results',
  levelCheckDesc: 'List of attempts and pass/fail status',
  open: 'Open →',

  // Tabs
  tabUsers: 'Users',
  tabBilling: 'Billing',
  tabApplications: 'Applications',
  tabSignups: 'Signups',

  // Users
  usersTitle: 'Users',
  usersSearchPlaceholder: 'Search by name or email',
  colName: 'Name',
  colEmail: 'Email',
  colNative: 'Native',
  colTarget: 'Learning',
  colLevel: 'Level',
  colOrg: 'Org',
  colSubStatus: 'Billing',
  filterAll: 'All',
  filterMembers: 'Individual',
  filterOrg: 'Organization',
  subNone: 'None',
  subActive: 'Active',
  subPaused: 'Paused',
  subCancelled: 'Cancelled',
  noUsers: 'No matching users',

  // Billing
  billingTitle: 'Billing',
  billingDesc: 'Users with an active subscription.',
  colPlan: 'Plan',
  colRemaining: 'Remaining',
  colRollover: 'Rollover',
  colPeriodEnd: 'Renews',
  noBilling: 'No active subscriptions',

  // Applications
  applicationsTitle: 'Organization Applications (pending)',
  applicationsNote: 'This is read-only. Approve or reject in the Word Pool admin screen.',
  colOrgName: 'Organization',
  colType: 'Type',
  colSchedulingMode: 'Scheduling',
  colApplicantUser: 'Applicant',
  colIdDocument: 'ID Doc',
  colCreatedAt: 'Submitted',
  hasIdDocument: 'Yes',
  noIdDocument: 'No',
  noApplications: 'No pending organization applications',
  goToWordPoolApprove: 'Approve in Word Pool admin →',

  // Signups
  signupsTitle: 'Community Signups',
  colSignupName: 'Name',
  colOrganizationName: 'Organization',
  colConsent: 'Publish consent',
  colStatus: 'Status',
  consentYes: 'Consented',
  consentNo: 'No',
  noSignups: 'No community signups',
  signupsUnavailable: 'The community signups table is unavailable or not accessible.',

  // Free mode (FREE_MODE) toggle
  freeModeTitle: 'Free Mode (billing on/off)',
  freeModeDesc: 'While free mode is ON, the plan screen hides all checkout flows and no checkout is created (prevents accidental charges). Turn it OFF to enable the regular paid plan selection.',
  freeModeCurrentLabel: 'Current state',
  freeModeOn: 'Free mode (billing OFF)',
  freeModeOff: 'Paid mode (billing ON)',
  freeModeTurnOff: 'Switch to paid mode (enable billing)',
  freeModeTurnOn: 'Switch to free mode (disable billing)',
  freeModeUpdating: 'Updating...',
  freeModeUpdateError: 'Failed to update free mode.',
  freeModeLoadError: 'Could not load free mode state (default: free).',

  // Matching: Google Meet auto-generation
  meetGenerateButton: 'Auto-generate Google Meet',
  meetGenerating: 'Generating...',
  meetGenerateSuccess: 'Google Meet link generated.',
  meetFallbackNotice: 'Auto-generation is disabled. Please enter a Meet link manually.',
  meetGenerateError: 'Failed to auto-generate Meet. Please enter a Meet link manually.',

  // Common states
  loading: 'Loading...',
  loadError: 'Failed to load data.',
  showingCount: '{{count}} items',

  // ── Word Pool review screen (admin/word-pool) ──
  wordPoolAdmin: {
    statusPending: 'Pending',
    statusApproved: 'Approved',
    statusRejected: 'Rejected',
    heading: 'Word Pool Review',
    adminLabel: 'Admin: ',
    toLevelCheck: 'Level check results →',
    filterAll: 'All',
    empty: 'No matching submissions',
    lessonLabel: 'Lesson: ',
    commentPlaceholder: 'Comment (optional)',
    approve: 'Approve',
    reject: 'Reject',
    commentLabel: 'Comment: ',
  },

  // ── Level check results screen (admin/level-check-results) ──
  levelCheckAdmin: {
    heading: 'Level Check Results',
    adminLabel: 'Admin: ',
    toWordPool: '← Word Pool admin',
    statTotal: 'Total attempts',
    statPassed: 'Passed',
    statFailed: 'Failed',
    statAvgScore: 'Average score',
    filterLanguage: 'Language:',
    filterLevel: 'Level:',
    filterPassed: 'Result:',
    filterAll: 'All',
    levelPrefix: 'Lv.',
    levelLabel: 'Level',
    empty: 'No matching results',
    passed: 'Passed',
    failed: 'Failed',
    member: 'Member',
    wrongQuestions: 'Incorrect: questions {{questions}}',
    scoreUnit: 'pts',
  },

  // ── Matching admin screen (admin/matching) ──
  matching: {
    confirmSuccess: 'Match confirmed! Emails have been sent to both users.',
    errorPrefix: 'Error: ',
    heading: 'Matching',
    adminLabel: 'Admin: ',
    toWordPool: 'Word Pool review →',
    toLevelCheck: 'Level check results →',
    pairConfirm: 'Confirm Pair',
    compatibilityScore: 'Compatibility score',
    scoreLang: 'Language',
    scoreTime: 'Time',
    scoreTz: 'TZ diff',
    breakdownLang: '① Language match (mutual benefit)',
    breakdownTime: '② Time-slot overlap',
    breakdownTz: '③ TZ diff (reference)',
    langFull: '✅ Full match',
    langPartial: '⚠️ One-way only',
    langNone: '❌ No match',
    slotsOverlap: '{{count}} overlapping slots',
    slotsNone: 'No overlap',
    tzDiff: 'UTC diff {{hours}}h',
    langScore: '{{score}} / 50 pts',
    timeScore: '{{score}} / 40 pts',
    tzScore: '{{score}} / 10 pts',
    choice: 'Choice #{{n}}: ',
    meetLinkLabel: 'Google Meet link',
    worksheetLabel: 'Worksheet PDF URL',
    worksheetHint: '(optional — if entered, the link is added to the email)',
    worksheetHelper: 'Upload the PDF to the worksheets bucket in Supabase Storage and paste the public URL',
    confirming: 'Confirming...',
    confirmPair: 'Confirm this pair',
    cancel: 'Cancel',
    empty: 'There are no requests waiting to be matched',
    selectTwo: 'Select two users to pair ({{count}} requests currently)',
    nativeBadge: 'Native: {{lang}}',
    learningBadge: 'Learning: {{lang}}',
    pointSuffix: ' pts',
    confirmedSlotLabel: 'Confirmed lesson time',
    confirmedSlotPlaceholder: 'Select a time (shared preference recommended)',
    slotOverlapSuffix: ' (shared)',
    slotNoOverlapWarn: '⚠ The two users have no shared preferred time. When choosing manually, confirm the other party\'s availability.',
    selectSlotFirst: 'Please select the confirmed lesson time first.',
    recommendTitle: 'Recommended pairs',
    recommendDesc: 'Auto-ranked by compatibility score. Use "Select this pair" to jump to the confirm panel.',
    selectThisPair: 'Select this pair',
    sharedSlotPrefix: 'Shared: ',
    noShared: 'No shared time',
    noRecommend: 'No recommended candidates',
    recommendScore: 'Compatibility {{score}}/100',
  },
};
